import React from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { ChartsAxisContentProps } from "@mui/x-charts/ChartsTooltip";

function formatDate(date: string | number | Date | null): string {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d.getTime())) return String(date);
    return d.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
    });
}

function renderValue(val: any): React.ReactNode {
    if (val == null) return "0";
    if (typeof val === "object" && "y" in val) {
        return String(val.y);
    }
    return String(val);
}

export function CustomTooltip(props: ChartsAxisContentProps) {
    const { series, axisValue, dataIndex } = props;
    const theme = useTheme();

    if (dataIndex == null) {
        return null;
    }

    return (
        <Box
            sx={{
                backgroundColor: theme.palette.common.white,
                borderRadius: "12px",
                boxShadow: "0px 12px 24px -4px #1018281a, 0px 4px 8px -2px #1018280f", // Very high elevation shadow per reference
                padding: "16px",
                minWidth: "200px",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                border: `1px solid ${theme.palette.divider}`,
            }}
        >
            <Typography
                variant="body2"
                sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    paddingBottom: "12px",
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                {formatDate(axisValue)}
            </Typography>

            {series.map((s) => {
                // Find the color and the value
                const color = typeof s.getColor === "function" ? s.getColor(dataIndex) : s.color;
                // The values are in s.data
                const value = s.data ? s.data[dataIndex] : null;
                if (value == null) return null;

                return (
                    <Box key={s.id} sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <Box
                                sx={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: "4px",
                                    backgroundColor: color,
                                    flexShrink: 0,
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    color: theme.palette.text.secondary,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                }}
                            >
                                <Box
                                    component="span"
                                    sx={{
                                        backgroundColor: theme.palette.grey[50] || "#f9fafb",
                                        padding: "2px 6px",
                                        borderRadius: "4px",
                                        fontWeight: 500,
                                        color: theme.palette.text.primary,
                                        border: `1px solid ${theme.palette.divider}`,
                                    }}
                                >
                                    {renderValue(value)}
                                </Box>{" "}
                                events
                            </Typography>
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}
